import cx from "classnames";

export type ClassMap<Keys extends string> = Record<
  Keys,
  Array<string> | Record<string, Array<string>>
>;
export type PresetRuleValue<Keys extends string> = Array<
  Partial<Keys> | Partial<Record<Keys, boolean>>
>;
export type Preset<
  ClassMapKeys extends string,
  PresetKeys extends string,
  Context
> = {
  [key in PresetKeys]: (ctx: Context) => PresetRuleValue<ClassMapKeys>;
};

export type AppliedPreset<PresetKeys extends string> = Record<
  PresetKeys,
  { class: string }
>;

/**
 * Utility class for composing CSS class names. Integrates with Primevue presets
 */
export class PresetHelper<
  Context,
  ClassMapKeys extends string,
  PresetKeys extends string
> {
  private readonly _classMap: ClassMap<ClassMapKeys>;
  readonly preset: Preset<ClassMapKeys, PresetKeys, Context>;

  /**
   * @param config.classMap
   * @description Object which defines sets of all class names that exist on each underlying DOM element of a component.
   *  Keys represent the identifier for the element, while the value can be one of two things:
   *   1) An array of strings representing class names.
   *   2) An object of key value pairs, where the value is an array of strings representing class names.
   *
   *  Convention for element identifiers (key) is '{prefix}__{componentName}_{subcomponentName}--{state?}'
   * @example
   *  {
   *      tai__dropdown_root: ['flex', 'flex-col', '...etc'],
   *      'tai__dropdown_root--open': [],
   *      'tai__dropdown_root--closed': [],
   *      tai__dropdown_menu: {
   *      // object option useful for semantic names:
   *          layout: [],
   *          color: []
   *      },
   *      'tai__dropdown_menu--open': [],
   *      'tai__dropdown_menu--closed': [],
   *  }
   * @param config.rules
   * @description Function determines which set of classes to apply to each identifier.
   *  Follows the conventions for CX library: https://www.npmjs.com/package/classnames
   * @example
   *  {
   *    root: (ctx: {isOpen: boolean}) => [
   *     'tai__dropdown_root',
   *     {
   *       'tai__dropdown_root--open': ctx.isOpen,
   *       'tai__dropdown_root--closed': !ctx.isOpen,
   *     }
   *   ],
   *   menu: ctx => [
   *     'tai_dropdown_menu',
   *     {
   *       'tai__dropdown_menu--open': ctx.isOpen,
   *       'tai__dropdown_menu--closed': !ctx.isOpen,
   *     }
   *   ]
   *  }
   */
  constructor(config: {
    classMap: ClassMap<ClassMapKeys>;
    preset: Preset<ClassMapKeys, PresetKeys, Context>;
  }) {
    this._classMap = config.classMap;
    this.preset = this.unmask(config.preset);
    this.applyPreset = this.applyPreset.bind(this);
  }

  /**
   * Replaces the references to the element identifiers (e.g. 'tai__dropdown_root') with actual class names described in config.classMap.
   * @param preset
   * @returns
   */
  private unmask(
    preset: Preset<ClassMapKeys, PresetKeys, Context>
  ): Preset<ClassMapKeys, PresetKeys, Context> {
    const output = {} as Preset<ClassMapKeys, PresetKeys, Context>;
    return Object.entries(preset).reduce(
      (acc, [key, fn]) => ({
        ...acc,
        [key]: (ctx: Context) => ({
          class: cx(
            (fn as (ctx: Context) => PresetRuleValue<ClassMapKeys>)(
              ctx || ({} as Context)
            )
          )
            .split(" ")
            .flatMap((node) => {
              const mapped = this._classMap[node as ClassMapKeys];
              if (!mapped) return [];
              if (Array.isArray(mapped)) return mapped;
              return Object.values(mapped).flat();
            })
            .join(" ")
        })
      }),
      output
    );
  }

  /**
   * Outputs the set of compiled classes from the unmasked preset.
   * @param ctx
   * @returns
   */
  public applyPreset(ctx: Context): AppliedPreset<PresetKeys> {
    const output = {} as AppliedPreset<PresetKeys>;
    return Object.entries(this.preset).reduce(
      (acc, [key, fn]) => ({
        ...acc,
        [key]: (fn as (ctx: Context) => { class: string })(ctx)
      }),
      output
    );
  }
}
